import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWizard } from 'react-use-wizard';
import { Check } from 'lucide-react';

import { Badge } from '@design-system';
import { cn } from '@client/lib';
import {
  ALL_POLICY_CATEGORIES,
  POLICY_CATEGORY_LABELS,
  POLICY_CATEGORY_DESCRIPTIONS,
} from '@shared/guidedPlanningPreferences';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import {
  useSession,
  useSessionMutations,
  useIsSessionComplete,
} from '@/features/guided-financial-planning/context';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

const POLICY_CATEGORY_OPTIONS = ALL_POLICY_CATEGORIES.map(category => ({
  id: category,
  label: POLICY_CATEGORY_LABELS[category],
  description: POLICY_CATEGORY_DESCRIPTIONS[category],
}));

interface PolicyCategoryCardProps {
  id: GuidedPlanningPolicyCategory;
  label: string;
  description: string;
  isSelected: boolean;
  isRecommended: boolean;
  onToggle: (id: GuidedPlanningPolicyCategory) => void;
  disabled?: boolean;
}

function PolicyCategoryCard({
  id,
  label,
  description,
  isSelected,
  isRecommended,
  onToggle,
  disabled,
}: PolicyCategoryCardProps) {
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      disabled={disabled}
      className={cn(
        'relative flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-primary/50 hover:bg-primary/5',
        isSelected ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white',
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200',
          isSelected ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white',
        )}
      >
        {isSelected && <Check className="size-4" />}
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-gray-900">{label}</span>
          {isRecommended && (
            <Badge
              variant="secondary"
              className="bg-primary/10 text-primary -mt-1 shrink-0 text-xs"
            >
              Predicted Preference
            </Badge>
          )}
        </div>
        <span className="mt-0.5 block text-sm text-gray-500">{description}</span>
      </div>
    </button>
  );
}

function usePolicyCategoriesFromSession(session: GuidedPlanningSessionResponse | undefined) {
  const sessionSelectedCategories = session?.stepData?.selectedPolicyCategories;
  const recommendedCategories = session?.recommendedPolicyCategories ?? [];

  const [selectedCategories, setSelectedCategories] =
    useState<Set<GuidedPlanningPolicyCategory> | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized && session) {
      if (sessionSelectedCategories && sessionSelectedCategories.length > 0) {
        setSelectedCategories(new Set(sessionSelectedCategories));
      } else if (recommendedCategories.length > 0) {
        setSelectedCategories(new Set(recommendedCategories));
      }
      setHasInitialized(true);
    }
  }, [session, sessionSelectedCategories, recommendedCategories, hasInitialized]);

  return {
    selectedCategories,
    setSelectedCategories,
    recommendedCategories,
  };
}

export function OptionsStep() {
  const { flags } = useFeatureFlags();
  const { nextStep, previousStep, isFirstStep } = useWizard();
  const { session } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();

  const { selectedCategories, setSelectedCategories, recommendedCategories } =
    usePolicyCategoriesFromSession(session);

  const handleToggleCategory = useCallback(
    (id: GuidedPlanningPolicyCategory) => {
      setSelectedCategories(prev => {
        const current = prev ?? new Set<GuidedPlanningPolicyCategory>();
        const nextSet = new Set(current);
        if (nextSet.has(id)) {
          nextSet.delete(id);
        } else {
          nextSet.add(id);
        }
        return nextSet;
      });
    },
    [setSelectedCategories],
  );

  const handleNext = useCallback(async () => {
    await updateStepData({
      selectedPolicyCategories: selectedCategories ? Array.from(selectedCategories) : [],
    });
    nextStep();
  }, [updateStepData, nextStep, selectedCategories]);

  const handleBack = useCallback(async () => {
    await updateStepData({
      selectedPolicyCategories: selectedCategories ? Array.from(selectedCategories) : [],
    });
    previousStep();
  }, [updateStepData, previousStep, selectedCategories]);

  const hasSelection = selectedCategories !== null && selectedCategories.size > 0;

  const sortedCategories = useMemo(() => {
    return [...POLICY_CATEGORY_OPTIONS].sort((a, b) => {
      const aSelected = selectedCategories?.has(a.id) ?? false;
      const bSelected = selectedCategories?.has(b.id) ?? false;
      const aRecommended = recommendedCategories.includes(a.id);
      const bRecommended = recommendedCategories.includes(b.id);

      // First sort by selected (selected items come first)
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then sort by recommended (within each group, recommended come first)
      if (aRecommended && !bRecommended) return -1;
      if (!aRecommended && bRecommended) return 1;

      return 0;
    });
  }, [selectedCategories, recommendedCategories]);

  const nextButtonLabel = flags.guidedPlanningPremiumBudgetStep
    ? 'Continue'
    : 'Show Me These Policies';

  return (
    <StepLayout
      title="Based on your preferences..."
      subtitle="We think these policy types would be a great fit. Feel free to adjust the selection to see different options."
      nextButtonLabel={nextButtonLabel}
      isNextDisabled={!hasSelection || isSaving}
      onNext={handleNext}
      onBack={handleBack}
      hideBack={isFirstStep}
      isLoading={isSaving}
    >
      <div className="mx-auto flex w-full max-w-lg flex-col gap-3">
        {sortedCategories.map(option => (
          <PolicyCategoryCard
            key={option.id}
            id={option.id}
            label={option.label}
            description={option.description}
            isSelected={selectedCategories?.has(option.id) ?? false}
            isRecommended={recommendedCategories.includes(option.id)}
            onToggle={handleToggleCategory}
            disabled={isSessionComplete}
          />
        ))}
      </div>
    </StepLayout>
  );
}
